import React from "react";
import PropTypes from "prop-types";
import { Link, graphql } from "gatsby";
import { getImage } from "gatsby-plugin-image";
import Seo from "../components/Seo";

import Layout from "../components/Layout";

import TrailFullWidthImage from "../components/TrailFullWidthImage";
import DisplayContentHeader from "../components/DisplayContentHeader";
import DisplayGallery from "../components/DisplayGallery";
import TrailDetails from "../components/TrailDetails";
import RiverDetails from "../components/RiverDetails";
// eslint-disable-next-line
export const PhotoGalleryPostTemplate = ({
  title,
  subtitle,
  image,
  meta,
  summary,
  gallery,
  helmet,
  slug,
  trail,
  stats,
}) => {
  const heroImage = getImage(image) || image;

  const showTitle = meta.showTitle;

  if (!subtitle) subtitle = "Photo Gallery";

  // build my array

  return (
    <div>
      {helmet || null}
      {showTitle ? (
        <TrailFullWidthImage
          img={heroImage}
          title={title}
          subheading={subtitle}
          detail={
            stats ? (
              <RiverDetails details={stats} />
            ) : (
              <TrailDetails details={trail} />
            )
          }
          imgPosition="center"
        />
      ) : (
        <TrailFullWidthImage img={heroImage} imgPosition="center" />
      )}

      <section className="section section--gradient">
        <div className="container">
          <div className="content">
            <div className="content">
              <DisplayContentHeader
                title={title}
                parent="Galleries"
                parentSlug="/galleries"
              />
            </div>
            <div className="content">
              <p>{summary}</p>
              {meta.suppressBooking !== true ? (
                <>
                  <p>
                    Ready to book? Book a{" "}
                    <Link to={slug} title={`Book a ${title} Shuttle`}>
                      {title} MTB Shuttle
                    </Link>{" "}
                    now.
                  </p>
                </>
              ) : null}
            </div>
            <div className="content">
              {gallery && gallery.images ? (
                <DisplayGallery gallery={gallery.images} />
              ) : null}
            </div>
            {meta.suppressBooking !== true ? (
              <div className="content">
                Don't miss out on the all the MTB fun riding {title}! Book a{" "}
                <Link to={slug} title={`Book a MTB Shuttle to ${title}`}>
                  MTB Shuttle to {title}
                </Link>{" "}
                today! Need more information? Get additional{" "}
                <Link to={slug} title={`${title} trail details`}>
                  {title} trail details
                </Link>{" "}
                now.
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
};

PhotoGalleryPostTemplate.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  image: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  meta: PropTypes.shape({
    showTitle: PropTypes.bool,
  }),
  summary: PropTypes.string,
  body: PropTypes.string,
  gallery: PropTypes.shape({
    images: PropTypes.arrayOf(
      PropTypes.shape({
        image: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
      })
    ),
  }),
  helmet: PropTypes.object,
  slug: PropTypes.string,
  trail: PropTypes.array,
};

const PhotoGalleryPost = ({ data }) => {
  const { markdownRemark: post } = data;

  let imageSrc =
    post.frontmatter.image.childImageSharp.gatsbyImageData.images.fallback.src;

  return (
    <Layout>
      <PhotoGalleryPostTemplate
        title={post.frontmatter.title}
        subtitle={post.frontmatter.subtitle}
        image={post.frontmatter.image}
        meta={post.frontmatter.meta}
        summary={post.frontmatter.summary}
        body={post.html}
        gallery={post.frontmatter.gallery}
        trail={post.frontmatter.trail}
        stats={post.frontmatter.stats}
        helmet={
          <Seo
            title={`Gallery > ${post.frontmatter.title}`}
            summary={post.frontmatter.summary}
            image={imageSrc}
            article={false}
            tags={[
              `${post.frontmatter.title} Gallery`,
              `${post.frontmatter.title} Photos`,
              `${post.frontmatter.title} Pictures`,
              `${post.frontmatter.title} Photo Gallery`,
            ]}
          />
        }
        slug={post.fields.slug}
      />
    </Layout>
  );
};

PhotoGalleryPost.propTypes = {
  data: PropTypes.object.isRequired,
};

export default PhotoGalleryPost;

export const photoGalleryPostQuery = graphql`
  query PhotoGalleryPost($id: String!) {
    markdownRemark(id: { eq: $id }) {
      html
      fields {
        slug
      }
      frontmatter {
        title
        subtitle
        image {
          childImageSharp {
            gatsbyImageData(
              quality: 100
              layout: FULL_WIDTH
              placeholder: BLURRED
            )
          }
        }
        meta {
          showTitle
          suppressBooking
        }
        stats {
          miles
          logo {
            childImageSharp {
              gatsbyImageData(
                quality: 100
                layout: FULL_WIDTH
                placeholder: BLURRED
              )
            }
          }
          map
          videos
          difficulty
          season
          reclevel
          permits
          permitsUrl
          putin
          takeout
        }
        trail {
          logo {
            childImageSharp {
              gatsbyImageData(
                quality: 100
                layout: FULL_WIDTH
                placeholder: BLURRED
              )
            }
          }
          miles
          descent
          elevationGain
          high
          low
          aveGrade
          maxGrade
          stravaIframeURL
          map
          videos
          related {
            trails {
              image {
                childImageSharp {
                  gatsbyImageData(
                    quality: 100
                    layout: FULL_WIDTH
                    placeholder: BLURRED
                  )
                }
              }
              title
              slug
            }
          }
        }
        gallery {
          images {
            image {
              childImageSharp {
                thumb: gatsbyImageData(
                  width: 320
                  height: 240
                  placeholder: BLURRED
                )
                full: gatsbyImageData(layout: FULL_WIDTH)
              }
            }
          }
        }
        summary
      }
    }
  }
`;
